import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '../state/AuthContext';
import { StatusBadge } from '../shell/StatusBadge';

export function SettingsAccountScreen() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onChangePassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api.authChangePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setMessage('Password updated.');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <section className="panel stack" data-testid="settings-account">
      <h2>Account</h2>
      <div className="settings-avatar-row">
        <div className="settings-avatar" aria-hidden="true">
          <span>
            {(user?.profile?.displayName || user?.fullName || user?.email || '?')
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((p) => p[0]?.toUpperCase() ?? '')
              .join('')}
          </span>
        </div>
        <div className="stack">
          <p>
            <strong>{user?.profile?.displayName || user?.fullName}</strong>
            <br />
            {user?.email}
          </p>
          <p>
            Status{' '}
            <StatusBadge tone={user?.emailVerifiedAt ? 'success' : 'warning'}>
              {user?.emailVerifiedAt ? 'Email verified' : 'Email pending verification'}
            </StatusBadge>
          </p>
          <Link className="btn secondary compact" to="/settings/profile">
            Edit profile &amp; avatar
          </Link>
        </div>
      </div>
      <form className="stack" onSubmit={onChangePassword}>
        <h3>Change password</h3>
        <div className="field">
          <label htmlFor="acct-current">Current password</label>
          <input
            id="acct-current"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="acct-new">New password</label>
          <input
            id="acct-new"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={10}
          />
        </div>
        {error ? <p className="error">{error}</p> : null}
        {message ? <p className="muted">{message}</p> : null}
        <button className="btn" type="submit">
          Update password
        </button>
      </form>
      <button type="button" className="btn secondary" onClick={() => void onLogout()}>
        Sign out
      </button>
    </section>
  );
}
